import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CTASection = () => {
  const benefits = [
    "No upfront costs or franchise fees",
    "Immediate access to qualified leads",
    "Comprehensive training and support",
    "Transparent 20% fee structure",
    "24/7 technical and business support",
    "Marketing and brand support included"
  ];

  const nextSteps = [
    {
      step: 1,
      title: "Submit Application",
      description: "Complete our online application in just 5 minutes",
      icon: "FileText"
    },
    {
      step: 2,
      title: "Background Check",
      description: "We verify your credentials and experience",
      icon: "Shield"
    },
    {
      step: 3,
      title: "Skills Assessment",
      description: "Quick technical evaluation and interview",
      icon: "CheckCircle"
    },
    {
      step: 4,
      title: "Start Earning",
      description: "Begin receiving jobs within 48 hours",
      icon: "TrendingUp"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-gradient-to-br from-primary/10 via-white to-secondary/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full">
                <Icon name="Rocket" size={16} className="text-primary mr-2" />
                <span className="text-sm font-medium text-primary">Ready to Get Started?</span>
              </div>
              
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-jakarta font-bold text-text-primary leading-tight">
                Join OpenbooQ Today
                <span className="block text-primary">Start Growing Tomorrow</span>
              </h2>
              
              <p className="text-lg lg:text-xl text-text-secondary leading-relaxed">
                Take the next step in your roofing career. Join hundreds of successful contractors 
                who are building thriving businesses with our platform.
              </p>
            </div>

            {/* Benefits List */}
            <div className="space-y-4">
              <h3 className="text-lg font-jakarta font-semibold text-text-primary">What You Get:</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {benefits?.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
                    <span className="text-text-secondary text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                variant="default" 
                size="lg"
                iconName="FileText"
                iconPosition="left"
              >
                Apply Now - It's Free
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                iconName="Phone"
                iconPosition="left"
              >
                Call (937) 910-8791
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="flex items-center space-x-6 pt-6 border-t border-border">
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} className="text-primary" />
                <span className="text-sm text-text-secondary">500+ Active Contractors</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Star" size={16} className="text-warning" />
                <span className="text-sm text-text-secondary">4.9/5 Contractor Rating</span>
              </div>
            </div>
          </div>

          {/* Process Steps */}
          <div className="space-y-8">
            <div className="bg-white rounded-2xl shadow-trust p-8 lg:p-12">
              <h3 className="text-2xl font-jakarta font-bold text-text-primary mb-8 text-center">
                Simple 4-Step Process
              </h3>
              
              <div className="space-y-6">
                {nextSteps?.map((step, index) => (
                  <div key={step?.step} className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon name={step?.icon} size={20} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="font-jakarta font-semibold text-text-primary">{step?.title}</h4>
                        <span className="px-2 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                          Step {step?.step}
                        </span>
                      </div>
                      <p className="text-text-secondary text-sm">{step?.description}</p>
                    </div>
                    {index < nextSteps?.length - 1 && (
                      <div className="absolute left-6 mt-12 w-0.5 h-6 bg-border"></div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-8 pt-6 border-t border-border text-center">
                <div className="inline-flex items-center space-x-2 text-sm text-success">
                  <Icon name="Clock" size={16} />
                  <span>Complete process in under 1 week</span>
                </div>
              </div>
            </div>

            {/* Contact Card */}
            <div className="bg-gradient-to-br from-secondary/10 to-accent/10 rounded-xl p-6">
              <h4 className="font-jakarta font-semibold text-text-primary mb-3">
                Have Questions?
              </h4>
              <p className="text-text-secondary text-sm mb-4">
                Our contractor success team is here to help you understand the process and get started.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Icon name="Phone" size={16} className="text-primary" />
                  <span className="text-sm text-text-primary">(937) 910-8791</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="Mail" size={16} className="text-primary" />
                  <span className="text-sm text-text-primary">contractors@openbooq.com</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Icon name="MessageCircle" size={16} className="text-primary" />
                  <span className="text-sm text-text-primary">Live chat available 24/7</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Stats */}
        <div className="mt-16 pt-12 border-t border-border">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-3xl lg:text-4xl font-jakarta font-bold text-primary">$15M+</div>
              <div className="text-text-secondary">Total Contractor Earnings</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl lg:text-4xl font-jakarta font-bold text-primary">2,500+</div>
              <div className="text-text-secondary">Jobs Completed</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl lg:text-4xl font-bold text-primary">98%</div>
              <div className="text-text-secondary">Customer Satisfaction</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl lg:text-4xl font-jakarta font-bold text-primary">24/7</div>
              <div className="text-text-secondary">Support Available</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;